import React, { useEffect, useState } from "react";
import Cookies from "universal-cookie";
import {
  addUrlParams,
  ButtonLink,
  urlCreateAccount,
} from "@userbrain/website-ui";

import imgInviteYourOwnRetroMac from "./images/inviteYourOwnRetroMac.jpg";
import imageTwoColumnsPresentationReadyReports from "./images/twoColumnsPresentationReadyReports.jpg";
import imageTwoColumnsAutomatedInsights from "./images/Automated-Insights-Newsletter.jpg";
import imageTwoColumnsAutomatedTranscripts from "./images/features-automated-transcripts-lg.jpg";
import imageTwoColumnsAndroidApp from "./images/features-android-app-lg.jpg";
import imageTwoColumnsMobileAppTesting from "./images/features-mobile-app-testing-lg.jpg";

import imagePascal from "./images/pascal.jpg";
import imageJamie from "./images/jamie.jpg";
import imageJoshua from "./images/joshua.jpg";
import imageAaron from "./images/aaron.jpg";
import imagePaul from "./images/paul.jpg";
import imageLiam from "./images/liam.jpg";
import imageJohannes from "./images/johannes.jpg";

export const urlCapterra = "https://www.capterra.com/p/141082/Userbrain/";
export const urlG2 = "https://www.g2.com/products/userbrain";

export const cookies = new Cookies();

export function getCreateAccountUrl(plan = undefined) {
  return addUrlParams(urlCreateAccount, { plan });
}

export const cookieDomain = process.env.GATSBY_DEFAULT_COOKIE_DOMAIN;

export const getTheRegionPromise = function () {
  if (typeof window !== "undefined") {
    return window.userbrainCookieDisclaimerRegionPromise || Promise.reject();
  } else {
    return Promise.reject();
  }
};

export const useLocalCurrencySymbol = function (fallbackValue = null) {
  const localCurrency = useLocalCurrency();
  switch (localCurrency) {
    case "EUR":
      return "€";
    case "USD":
      return "$";
    case "GBP":
      return "£";
    default:
      return fallbackValue;
  }
};

export const useLocalCurrency = function (initialState = null) {
  const [currency, setCurrency] = useState(initialState);
  useEffect(() => {
    getTheRegionPromise()
      .then((region) => {
        switch (region) {
          case "eu":
            setCurrency("EUR");
            break;
          case "gb":
            setCurrency("GBP");
            break;
          default:
            setCurrency("USD");
        }
      })
      .catch(() => {
        setCurrency("USD");
      });
  }, []);
  return currency;
};

export const useIsScrolledPast = function (ref) {
  const [isScrolledPast, setIsScrolledPast] = useState(false);
  useEffect(() => {
    function handleScroll() {
      if (ref.current) {
        setIsScrolledPast(window.scrollY >= ref.current.offsetTop);
      }
    }

    handleScroll();
    window.addEventListener("scroll", handleScroll);
    return () => {
      window.removeEventListener("scroll", handleScroll);
    };
  }, [ref]);
  return isScrolledPast;
};

/* This should go to design system countDecimals formatNumber formatPrice formatPriceWithBillingCycle*/
const toLocaleStringSupportsLocales = (() => {
  const number = 0;
  try {
    number.toLocaleString("i");
  } catch (e) {
    return e.name === "RangeError";
  }
  return false;
})();

const countDecimals = function (value) {
  if (Math.floor(value) === value) return 0;
  return value.toString().split(".")[1]?.length || 0;
};

export function formatNumber(number) {
  return number.toLocaleString();
}

export function formatPrice(number, currency, minimalVersion = false) {
  number = Math.round(parseFloat(number) * 100) / 100;
  currency = currency ? currency : "USD";
  if (toLocaleStringSupportsLocales && !minimalVersion) {
    return number.toLocaleString(undefined, {
      style: "currency",
      currency: currency.toUpperCase(),
      minimumFractionDigits: countDecimals(number) > 0 ? 2 : 0,
    });
  } else {
    if (currency.toUpperCase() === "EUR") {
      return number.toLocaleString() + "€";
    } else if (currency.toUpperCase() === "USD") {
      return "$" + number.toLocaleString();
    } else if (currency.toUpperCase() === "GBP") {
      return "£" + number.toLocaleString();
    } else {
      return number.toLocaleString() + currency;
    }
  }
}

export const twoColumnsInviteYourOwn = {
  imgSrc: imgInviteYourOwnRetroMac,
  imgAlt: "Old Apple computer with a pixalated open mail envelope.",
  overline: "Invite your own testers",
  heading: "Want to invite your own testers? No probs!",
  content:
    "With Userbrain, you can test with your own customers, friends, participants, or whoever else you’d like to invite to the party by simply dropping them a link via text or email.",
  linkText: "More on inviting your own testers",
  linkTo: "/features/invite-your-own-testers",
};

export const twoColumnsPresentationReadyReports = {
  imgSrc: imageTwoColumnsPresentationReadyReports,
  imgAlt: "Test reports to share with your team",
  heading: "Test Reports",
  content:
    "Get the right information to the right people at the right time with our stunning user test reports.",
  linkText: "Learn more about reports",
  linkHref:
    "https://www.userbrain.com/blog/new-feature-get-better-insights-with-reports/",
};

export const twoColumnsMobileAppTesting = {
  imgSrc: imageTwoColumnsMobileAppTesting,
  imgAlt:
    "A smartphone with a simple car racing game on it called Test Racing Cars",
  heading: "Mobile App Testing",
  content:
    "See how real people interact with your mobile app on both iPhone and Android devices, uncover unknown bugs, and validate your design choices with real feedback.",
  linkText: "Learn more about mobile app testing",
  linkHref:
    "https://www.userbrain.com/blog/mobile-app-user-testing-for-android-ios-apps/",
};

export const twoColumnsAutomatedInsights = {
  imgSrc: imageTwoColumnsAutomatedInsights,
  imgAlt: "A magic wand and some automatically created video comments",
  heading: "AI Insights",
  content:
    "Introducing Userbrain’s Automated Insights feature, a game-changer for UX professionals looking to optimize their user test analysis process.",
  linkText: "Learn more about automated insights",
  linkHref:
    "https://www.userbrain.com/blog/ai-clips-instant-key-moments-from-your-user-test-ready-to-share/",
};

export const twoColumnsAutomatedTranscripts = {
  imgSrc: imageTwoColumnsAutomatedTranscripts,
  imgAlt: "A transcript to help analyze a user testing video",
  heading: "Transcripts",
  content:
    "Gone are the days of manual transcription, thanks to AI! Our new automated transcripts feature makes analyzing user feedback faster and easier than ever before.",
  linkText: "Learn more about transcripts",
  linkHref:
    "https://www.userbrain.com/blog/automated-transcripts-faster-user-tests-with-ai-transcription/",
};

export const twoColumnsAndroidApp = {
  imgSrc: imageTwoColumnsAndroidApp,
  imgAlt: "An Android phone with the Userbrain recording app",
  heading: "Android App",
  content:
    "We recently introduced Android support. The Userbrain Recorder Android app is now available for testers in the Google Play store. ‘Appy days!",
  linkText: "Learn more about Android user testing",
  linkHref:
    "https://www.userbrain.com/blog/introducing-android-user-testing-userbrain-launches-android-app/",
};

export const testimonialPascal = {
  text: "“Userbrain gives me incredibly useful insights on how first-time users understand the value proposition of our product and helps me identify bugs or usability issues quickly.”",
  image: imagePascal,
  alt: "Portrait photo of Pascal Briod",
  name: "Pascal Briod",
  position: "Head of Product & Co-Founder at Monito",
  viaHref:
    "https://medium.com/monito-stories/the-11-digital-tools-i-use-every-week-as-head-of-product-8163a5193647",
  viaName: "Medium",
};

export const testimonialJamie = {
  text: "“Userbrain has enabled us to run user testing that is neutral, insightful and that can be clearly actioned.”",
  image: imageJamie,
  alt: "Portrait photo of Jamie Fisher",
  name: "Jamie Fisher",
  position: "Head of Ecommerce at Bradshaw Taylor Ltd.",
  viaHref: urlCapterra,
  viaName: "Capterra",
};

export const testimonialJoshua = {
  title: "Best in industry for easy user testing",
  text: "“From start to finish it’s been absolutely phenomenal. I really do enjoy how easy it is to utilize this software, not only is it simple but extremely easy to learn in very straightforward.”",
  image: imageJoshua,
  alt: "Portrait photo of Joshua A.",
  name: "Joshua A.",
  position: "CEO",
  viaHref: urlCapterra,
  viaName: "Capterra",
};

export const testimonialDamon = {
  title: "Easy and fast",
  text: "“Really quick to get started and the interface is super user friendly. Also, the responses starting coming in within an hour of test launch and the quality of the respondents seems pretty good.”",
  name: "Damon B.",
  viaHref: urlCapterra,
  viaName: "Capterra",
};

export const testimonialThomas = {
  title: "Excellent service and very easy to use",
  text: "“Really like the experience, and happy to continue using this service. This is really easy to use, and you can get the videos within a matter of hours after deciding to test. The customer service is excellent, with the team getting back to you really quickly - and happy to give you replacement tests if any don’t work out technically.”",
  name: "Thomas L.",
  position: "Head of Communications",
  viaHref: urlCapterra,
  viaName: "Capterra",
};

export const testimonialPaul = {
  title: "Userbrain is elegantly simple",
  text: "“The customer service has been excellent — in the rare case we had a bad user test, they quickly refunded the credit.”",
  image: imagePaul,
  alt: "Portrait photo of Paul Doerwald",
  name: "Paul Doerwald",
  position: "Founder and CEO of Clockk",
  viaHref:
    "https://www.g2.com/products/userbrain/reviews/userbrain-review-7554640",
  viaName: "G2",
};

export const testimonialAaron = {
  title: "There really isn’t anything I don’t like about Userbrain.",
  text: "“I can dip in and out with pay-as-you-go credits with no commitment which I do for personal projects, and I can subscribe to make a real saving by buying monthly. It’s the best of both worlds.”",
  image: imageAaron,
  alt: "Portrait photo of Aaron Coates",
  name: "Aaron Coates",
  position: "Chief Product Officer at Money Alive",
  viaHref: "https://www.capterra.com/p/141082/Userbrain/reviews/4490562/",
  viaName: "Capterra",
};

export const testimonialG2_1 = {
  title: "Userbrain delivers remote usability tests super fast",
  text: "“Doing usability tests always meant a lot of work in finding the correct participants to test, coordinating their availability, managing changes, etc. With Userbrain, we don't have to care about this!”",
  name: "Reviewer on G2",
  viaHref:
    "https://www.g2.com/products/userbrain/reviews/userbrain-review-8633563",
  viaName: "G2",
};
export const testimonialLiam = {
  title: "Userbrain’s screening feature has been amazing for us",
  text: "“The ability to screen our demographic selection has been such a big relief. We previously did a lot of screening within the interview process of user testing, hence the amount of people who would get through and be ineligible was high and wasted credits.”",
  name: "Liam Copping",
  image: imageLiam,
  alt: "Portrait photo of Liam Copping",
  position: (
    <>
      Founder of{" "}
      <ButtonLink
        customTag="a"
        rel="noreferrer"
        target="_blank"
        href="https://prosper-labs.co.uk/"
      >
        Prosper Labs
      </ButtonLink>
    </>
  ),
};

export const testimonialJohannes = {
  title: "Impressed by Userbrain’s panel of participants",
  text: "“We are a special case where we need people who have knowledge of Excel, and some basic knowledge of SQL servers. So I thought it was quite hard to find the right participants, but I’m really impressed by Userbrain. The panel of testers you have, how easy and fast it is to find qualified testers that are doing a really good job.”",
  name: "Johannes Åkesson",
  image: imageJohannes,
  alt: "Portrait photo of Johannes Åkesson",
  position: (
    <>
      Founder of{" "}
      <ButtonLink
        customTag="a"
        rel="noreferrer"
        target="_blank"
        href="https://www.sqlspreads.com/"
      >
        SQL Spreads
      </ButtonLink>
    </>
  ),
};

export const testimonialRyK = {
  title: "Userbrain is a great tool for remote usability testing",
  text: "“I’m a solo designer, so cost is the number one barrier for me while conducting user research. Userbrain is incredibly affordable, and I was blown away by how quickly my research request filled up and by how high quality the results were.”",
  name: "Ry K.",
  viaHref: "https://www.capterra.com/p/141082/Userbrain/#Capterra___3307635",
  viaName: "Capterra",
};

export function setTrafficSource(cvalue) {
  const exdays = 365;
  const expires = new Date();
  expires.setTime(expires.getTime() + exdays * 24 * 60 * 60 * 1000);
  cookies.set("ub_src", cvalue, { path: "/", domain: cookieDomain, expires });
}

export function setRefcode(cvalue) {
  const exdays = 365;
  const expires = new Date();
  expires.setTime(expires.getTime() + exdays * 24 * 60 * 60 * 1000);
  cookies.set("refcode", cvalue, { path: "/", domain: cookieDomain, expires });
}

export const useClickOutside = (ref, callback) => {
  const handleClick = (e) => {
    if (ref.current && !ref.current.contains(e.target)) {
      callback();
    }
  };
  React.useEffect(() => {
    document.addEventListener("click", handleClick);
    return () => {
      document.removeEventListener("click", handleClick);
    };
  });
};

export function extractContainerProps(props) {
  const { style, onClick, innerRef } = props;
  return { style, onClick, ref: innerRef };
}

export function pluralizer(singular, plural = (singular) => singular + "s") {
  return function (count, includeCount = false) {
    let result;
    if (count === 1) {
      result = singular;
    } else {
      result = typeof plural === "function" ? plural(singular) : plural;
    }
    if (includeCount) {
      result = `${count} ${result}`;
    }
    return result;
  };
}
